// components/FloatingAIBuilder.tsx
'use client';

import { useEffect, useRef } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { gsap } from 'gsap';
import { Sparkles } from 'lucide-react';

export default function FloatingAIBuilder() {
    const router = useRouter();
    const pathname = usePathname();
    const btnRef = useRef<HTMLButtonElement>(null);
    const pulseRef = useRef<HTMLSpanElement>(null);
    const labelRef = useRef<HTMLDivElement>(null);

    const isOnBuilderPage = pathname === '/ai-product-builder';
    const hasMounted = useRef(false);

    // ── Prefetch the target route so navigation is instant ──
    useEffect(() => {
        router.prefetch('/ai-product-builder');
    }, [router]);

    // ── Show / hide the button based on route ──
    // IMPORTANT: this component stays mounted at all times now (no more
    // `return null`). Unmounting was the actual bug — it destroyed the
    // button's ref, so when you navigated back the "entry" animation
    // (which only ever fires once) never ran again, leaving the icon
    // stuck at opacity 0 forever while the pulse ring kept animating.
    useEffect(() => {
        if (!btnRef.current) return;

        if (isOnBuilderPage) {
            gsap.to(btnRef.current, {
                opacity: 0,
                scale: 0.5,
                x: 40,
                duration: 0.3,
                ease: 'power2.in',
            });
        } else if (!hasMounted.current) {
            // first paint on a non-builder page: animate in from offscreen
            gsap.fromTo(
                btnRef.current,
                { x: 80, opacity: 0, scale: 0.5 },
                {
                    x: 0,
                    opacity: 1,
                    scale: 1,
                    duration: 0.8,
                    ease: 'back.out(1.7)',
                    delay: 0.4,
                }
            );
        } else {
            // returning to a non-builder page: animate back in
            gsap.to(btnRef.current, {
                opacity: 1,
                scale: 1,
                x: 0,
                duration: 0.5,
                ease: 'back.out(1.7)',
            });
        }

        hasMounted.current = true;
    }, [isOnBuilderPage]);

    // ── Continuous pulse glow ──
    useEffect(() => {
        if (isOnBuilderPage) {
            gsap.set(pulseRef.current, { opacity: 0 });
            return;
        }

        const tl = gsap.timeline({ repeat: -1 });
        tl.to(pulseRef.current, {
            scale: 1.8,
            opacity: 0,
            duration: 1.2,
            ease: 'power2.out',
        }).to(pulseRef.current, { scale: 1, opacity: 0.4, duration: 0 });

        return () => { tl.kill(); };
    }, [isOnBuilderPage]);

    // ── Label hover animation ──
    const handleMouseEnter = () => {
        gsap.to(labelRef.current, {
            opacity: 1,
            x: 0,
            duration: 0.3,
            ease: 'power2.out',
        });
        gsap.to(btnRef.current, {
            scale: 1.1,
            duration: 0.2,
            ease: 'power2.out',
        });
    };

    const handleMouseLeave = () => {
        gsap.to(labelRef.current, {
            opacity: 0,
            x: 10,
            duration: 0.2,
            ease: 'power2.in',
        });
        gsap.to(btnRef.current, {
            scale: 1,
            duration: 0.2,
            ease: 'power2.out',
        });
    };

    // ── Click: navigate immediately, animate in parallel ──
    // (previously navigation only fired inside a chained onComplete,
    // which could get dropped if the tween was interrupted — that's
    // what caused the "click does nothing but play an effect" bug)
    const handleClick = () => {
        if (isOnBuilderPage) return;

        router.push('/ai-product-builder');

        gsap.to(btnRef.current, {
            scale: 0.85,
            duration: 0.1,
            ease: 'power2.in',
            onComplete: () => {
                gsap.to(btnRef.current, {
                    scale: 1,
                    duration: 0.2,
                    ease: 'back.out(2)',
                });
            },
        });
    };

    return (
        <div
            className={`fixed right-4 bottom-6 z-50 ${isOnBuilderPage ? 'pointer-events-none' : ''
                }`}
        >
            <div
                className="relative flex items-center"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                {/* Hover Label */}
                <div
                    ref={labelRef}
                    style={{ opacity: 0, transform: 'translateX(10px)' }}
                    className="absolute right-20 bg-zinc-900 text-amber-100 text-sm
                     font-semibold px-4 py-2 rounded-xl shadow-2xl
                     whitespace-nowrap pointer-events-none
                     border border-amber-400/20"
                >
                    ✨ AI Product Builder
                    {/* Arrow */}
                    <div
                        className="absolute left-full top-1/2 -translate-y-1/2
                       border-8 border-transparent border-l-zinc-900"
                    />
                </div>

                {/* Pulse Ring — gold instead of purple */}
                <span
                    ref={pulseRef}
                    className="absolute inset-0 rounded-full bg-amber-400 opacity-40
                     pointer-events-none"
                />

                {/* Main Button — black/gold premium gradient */}
                <button
                    ref={btnRef}
                    onClick={handleClick}
                    aria-label="Open AI Product Builder"
                    style={{ opacity: 0 }}
                    className="relative w-16 h-16 rounded-full shadow-2xl
                     flex items-center justify-center
                     bg-gradient-to-br from-zinc-900 via-neutral-800 to-black
                     ring-2 ring-amber-400/60 hover:ring-amber-400
                     cursor-pointer outline-none border-none
                     transition-all"
                >
                    <Sparkles className="w-7 h-7 text-amber-300" />
                </button>
            </div>
        </div>
    );
}